import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTrigger } from '../../../Dialog';
import { useTranslation } from '~/shared/utils/translation';
import { useFrame } from '~/shared/utils';
import {
    DialogContainer,
    StyledFormWrapper,
    StyledInput,
    Label,
    Paragraph,
    StyledButton,
    StyledCheckmark,
    StyledSubmitSuccess,
    TriggerButton,
    StyledEmailPolicy,
    StyledContentWrapper,
} from './styled';
import { Link } from '~/shared/components/Link';
import { theme } from '~/theme';
import { useNotifyWhenBack } from './api';
import { useForm } from 'react-hook-form';
import { Form } from '~/shared/components/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useValidationSchema } from './lib/useValidationSchema';
import Spinner from '~/shared/components/Spinner/Spinner';

type NotifyWhenBackProps = {
    CTAText: string;
    productId: string;
};

export const NotifyWhenBack = ({ CTAText, productId }: NotifyWhenBackProps) => {
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const [isSubmitSuccessFul, setIsSubmitSuccessFul] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isSubmitError, setIsSubmitError] = useState(false);
    const validationSchema = useValidationSchema();

    const methods = useForm<any>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema),
    });

    const {
        formState: { isValid },
    } = methods;

    const emailValue = methods.watch('email');
    const { reset } = methods;

    const notifyMeData = {
        email: emailValue,
        productId: productId,
        source: 'web',
    };

    const { refetch, isFetching } = useNotifyWhenBack(notifyMeData);

    const handleSubmit = async () => {
        const { data } = await refetch();
        if (data?.isFetchSuccessful) {
            setIsSubmitSuccessFul(true);
        } else {
            setIsSubmitError(true);
        }
    };

    const handleClose = () => {
        setIsSubmitSuccessFul(false);
        setIsSubmitError(false);
        setOpenDialog(false);
    };

    return (
        <Dialog
            open={openDialog}
            onOpenChange={(value) => {
                reset();
                setOpenDialog(value);
            }}
        >
            <DialogTrigger asChild>
                <TriggerButton variant="pdp">{CTAText}</TriggerButton>
            </DialogTrigger>
            <DialogContent>
                <DialogContainer>
                    {isSubmitError ? (
                        <StyledContentWrapper>
                            <h2>{translate('pdp.notifyBannerErrorTitle')}</h2>
                            <div>{translate('pdp.notifyBannerErrorInfo')}</div>
                            <StyledButton onClick={handleClose}>
                                {translate('pdp.notifyBannerContinue')}
                            </StyledButton>
                        </StyledContentWrapper>
                    ) : isSubmitSuccessFul ? (
                        <StyledSubmitSuccess>
                            <StyledContentWrapper>
                                <StyledCheckmark />
                                <h2> {translate('pdp.notifyBannerSuccessTitle')}</h2>
                                <div>{translate('pdp.notifyBannerSuccessInfo')}</div>
                            </StyledContentWrapper>
                            <StyledButton onClick={handleClose}>
                                {translate('pdp.notifyBannerContinue')}
                            </StyledButton>
                        </StyledSubmitSuccess>
                    ) : (
                        <>
                            <h2> {translate('pdp.notifyBannerTitle')}</h2>
                            <Paragraph>{translate('pdp.notifyBannerDescription')}</Paragraph>
                            <StyledFormWrapper>
                                <Form methods={methods} onSubmit={handleSubmit}>
                                    <Label style={{ display: 'flex' }}>Email</Label>
                                    <StyledInput
                                        appendWhenValid={false}
                                        name={'email'}
                                        maxLength={50}
                                        required
                                    />
                                    {isFetching ? (
                                        <Spinner />
                                    ) : (
                                        <StyledButton type="submit" disabled={!isValid}>
                                            {translate('pdp.notifyBannerSubmit')}
                                        </StyledButton>
                                    )}
                                </Form>
                            </StyledFormWrapper>

                            <StyledEmailPolicy>
                                <span>{translate('pdp.notifyEmailPolicy')}</span>
                                <Link
                                    color={theme.colors.black}
                                    noHover
                                    target="_blank"
                                    title={translate('pdp.notifyEmailPolicyLink')}
                                    href={frame.market?.personalDataPolicyLink}
                                >
                                    <span style={{ textDecoration: 'underline' }}>
                                        {translate('pdp.notifyEmailPolicyLink')}
                                    </span>
                                </Link>
                            </StyledEmailPolicy>
                        </>
                    )}
                </DialogContainer>
            </DialogContent>
        </Dialog>
    );
};
